import * as THREE from 'three';
import { useMapStore } from '@/stores/mapStore.ts'
import { zoomFactor } from '@/enums/zoomFactor.ts'
import Eta from '@/models/eta.ts'

export class MapETA {

  private parent: THREE.Line
  private mapStore: any = useMapStore();
  private spriteGroup: THREE.Group;
  private spriteDataArray: Array<{
    sprite: THREE.Sprite;
    canvasWidth: number;
    canvasHeight: number;
  }> = [];

  public constructor(parent: THREE.Line) {
    this.parent = parent;

    const positions = this.parent.geometry.attributes.position.array;
    const numPoints = positions.length / 3;
    const eta = new Eta();

    // Create a group to hold all sprites
    this.spriteGroup = new THREE.Group();

    for (let i = 0; i < numPoints - 1; i++) {
      const startX = positions[i * 3];
      const startY = positions[i * 3 + 1];

      const endX = positions[(i + 1) * 3];
      const endY = positions[(i + 1) * 3 + 1];

      eta.start(startX, startY);
      const days = eta.etaNext(endX, endY, this.mapStore.etaSpeed);

      // Only create sprite if days > 0
      if (days > 0) {
        // Calculate midpoint and angle of the line
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;
        const midPoint = new THREE.Vector3(midX, midY, 0.2);
        const deltaX = endX - startX;
        const deltaY = endY - startY;
        const angle = Math.atan2(deltaY, deltaX);

        // Create a canvas to draw the text
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if(context) {
          const fontSize = 64;
          context.font = `${fontSize}px Arial`;
          const text = days + '';
          const metrics = context.measureText(text);
          // Set canvas dimensions based on text size
          canvas.width = metrics.width;
          canvas.height = fontSize;
          context.font = `${fontSize}px Arial`;
          context.fillStyle = 'rgba(255, 255, 255, 1.0)';
          context.textBaseline = 'middle';
          context.fillText(text, 0, canvas.height / 2);

          // Create a texture from the canvas
          const texture = new THREE.CanvasTexture(canvas);

          // Create sprite material
          const spriteMaterial = new THREE.SpriteMaterial({ map: texture, transparent: true });

          // Create sprite and set its position and rotation
          const sprite = new THREE.Sprite(spriteMaterial);
          sprite.position.copy(midPoint);

          // Determine scale based on zoom factor
          const scale = this.getScaleForZoom(this.mapStore.zoomFactor);
          sprite.scale.set(scale * canvas.width, scale * canvas.height, 1);

          let adjustedAngle = angle;
          if (adjustedAngle > Math.PI / 2) {
            adjustedAngle -= Math.PI;
          }
          if (adjustedAngle < -Math.PI / 2) {
            adjustedAngle += Math.PI;
          }
          sprite.material.rotation = adjustedAngle;

          // Store sprite data for later updates
          this.spriteDataArray.push({
            sprite: sprite,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height
          });

          // Add sprite to the group
          this.spriteGroup.add(sprite);
        }
      }
    }
    this.parent.add(this.spriteGroup);
  }

  private getScaleForZoom(zoom: number): number {
    // Use fixed scale values at specific zoom levels for better appearance
    if (zoom === zoomFactor.Planet) {
      return 0.01;
    } else if (zoom === zoomFactor.Solar) {
      return 0.025;
    } else if (zoom === zoomFactor.Local) {
      return 0.09;
    } else if (zoom === zoomFactor.Sector) {
      return 0.20;
    } else if (zoom === zoomFactor.Quadrant) {
      return 0.40;
    } else if (zoom === zoomFactor.Max) {
      return 0.75;
    } else {
      // For intermediate zoom levels, calculate scale based on nearest preset zoom levels
      if (zoom > zoomFactor.Solar && zoom < zoomFactor.Planet) {
        // Between Solar and Planet
        const percent = (zoom - zoomFactor.Solar) / (zoomFactor.Planet - zoomFactor.Solar);
        return 0.025 + (0.01 - 0.025) * percent;
      } else if (zoom > zoomFactor.Local && zoom < zoomFactor.Solar) {
        // Between Local and Solar
        const percent = (zoom - zoomFactor.Local) / (zoomFactor.Solar - zoomFactor.Local);
        return 0.09 + (0.025 - 0.09) * percent;
      } else if (zoom > zoomFactor.Sector && zoom < zoomFactor.Local) {
        // Between Sector and Local
        const percent = (zoom - zoomFactor.Sector) / (zoomFactor.Local - zoomFactor.Sector);
        return 0.20 + (0.09 - 0.20) * percent;
      } else if (zoom > zoomFactor.Quadrant && zoom < zoomFactor.Sector) {
        // Between Quadrant and Sector
        const percent = (zoom - zoomFactor.Quadrant) / (zoomFactor.Sector - zoomFactor.Quadrant);
        return 0.40 + (0.20 - 0.40) * percent;
      } else if (zoom > zoomFactor.Max && zoom < zoomFactor.Quadrant) {
        // Between Max and Quadrant
        const percent = (zoom - zoomFactor.Max) / (zoomFactor.Quadrant - zoomFactor.Max);
        return 0.75 + (0.40 - 0.75) * percent;
      }
    }
    return 0.01; // Default fallback
  }

  public updateZoom(newZoom: number): void {
    const newScale = this.getScaleForZoom(newZoom);
    
    for (const spriteData of this.spriteDataArray) {
      spriteData.sprite.scale.set(
        newScale * spriteData.canvasWidth,
        newScale * spriteData.canvasHeight,
        1
      );
    }
  }

  public dispose(): void {
    // Clean up sprites and materials
    for (const spriteData of this.spriteDataArray) {
      if (spriteData.sprite.material.map) {
        spriteData.sprite.material.map.dispose();
      }
      spriteData.sprite.material.dispose();
    }
    this.spriteDataArray = [];
    
    if (this.parent && this.spriteGroup) {
      this.parent.remove(this.spriteGroup);
    }
  }

}
